/**
 * Bulk-decrypt the private rows in a list of activities, returning a
 * Map<id, PrivatePayload>. Rows that fail to decrypt (corrupted ciphertext,
 * wrong DEK) are silently skipped — the caller treats their absence as
 * "no plaintext available" rather than an error, matching how the UI
 * already displays undecryptable rows.
 *
 * Originally duplicated in Home.svelte and TagPage.svelte; consolidated
 * here so any future change (parallelism, telemetry on decrypt failures)
 * lives in one place.
 */
import { decryptPayload, base64ToBytes, type PrivatePayload } from './crypto';
import type { Activity } from '../../../shared/types';

export function decryptPrivateCleartext(
  activities: Activity[],
  dek: Uint8Array | null,
): Map<string, PrivatePayload> {
  const map = new Map<string, PrivatePayload>();
  if (!dek) return map;
  for (const a of activities) {
    if (a.visibility !== 'private') continue;
    try {
      map.set(
        a.id,
        decryptPayload(
          { ciphertext: base64ToBytes(a.ciphertext), nonce: base64ToBytes(a.nonce) },
          dek,
        ),
      );
    } catch {
      // Skip rows we can't decrypt — UI shows them as "log in again" via
      // the !session.dek branch in ActivityRow.
    }
  }
  return map;
}
